import { useState, useEffect, useRef } from 'react'
import RecordHeader from './RecordHeader';
import RecordContent from './RecordContent';
import VoiceCloneModal from './VoiceCloneModal';
import VoiceSettingsModal from './VoiceSettingsModal';
import RewriteModal from './RewriteModal';
import { updateHistoryRecord, updateRewriteResult } from '../../utils/storage';
import { dispatchCreditUpdateEvent, startUseCredit } from '../../utils/creditSystem';
import RecordUrl from './RecordUrl';
import RewriteResultsList from './RewriteResultsList';
import { generateVoice, rewriteContent } from '../../utils/api';
import RecordDownload from './RecordDownload';

function HistoryRecord({ record, isExpanded, isSelected, onToggle, onSelect, onCopy, onUpdateContent, rewriteResult, myVoices, hasApiKey, showAvatarGenerateModel, currentPlayingId, isPlaying, onPlayVoice }) {

    const [isEditing, setIsEditing] = useState(false);
    const [editedContent, setEditedContent] = useState(record.content);
    const [isSaving, setIsSaving] = useState(false);
    const [editingRewriteId, setEditingRewriteId] = useState(0);
    const [editingRewriteIndex, setEditingRewriteIndex] = useState(-1);
    const [editedRewriteContent, setEditedRewriteContent] = useState('');
    const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
    const [isVoiceSettingsModalOpen, setIsVoiceSettingsModalOpen] = useState(false);
    const [voiceSettingsId, setVoiceSettingsId] = useState('');
    const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
    const [isRewriteModalOpen, setIsRewriteModalOpen] = useState(false);
    const [isRewriting, setIsRewriting] = useState(false);
    const [excelUrl, setExcelUrl] = useState(null);

    useEffect(() => {
        // Reset edited content when record changes or when expanding/collapsing
        setEditedContent(record.content);
        setIsEditing(false);
        setEditingRewriteIndex(-1);
        setEditedRewriteContent('');
    }, [record.content, isExpanded]);

    useEffect(() => {
        if (record.type == 'pdf_to_excel') {
            const blob = new Blob([record.content], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            
            const url = URL.createObjectURL(blob);
            const fileName = record.title.replace('.pdf', '.xlsx');
            
            setExcelUrl({
                url: url,
                fileName: fileName,
                blob: blob
            });
        }
    }, []);

    try {

        // Determine record type based on URL
        const isYoutube = record.source?.includes('youtube.com') || record.source?.includes('youtu.be');
        const recordType = isYoutube ? 'youtube' : 'pdf';
        
        const handleEditToggle = (e) => {
            e.stopPropagation();
            setIsEditing(!isEditing);
            if (!isEditing) {
                setEditedContent(record.content);
            }
        };

        const handleSave = async (e) => {
            e.stopPropagation();
            setIsSaving(true);
            
            try {
                await onUpdateContent(record.id, editedContent);
                setIsEditing(false);
            } catch (error) {
                console.error('Error saving content:', error);
            } finally {
                setIsSaving(false);
            }
        };

        const handleContentChange = (e) => {
            setEditedContent(e.target.value);
        };

        const handleRewriteEditToggle = (e, index, id) => {
            e.stopPropagation();
            if (editingRewriteIndex === index) {
                setEditingRewriteIndex(-1);
                setEditedRewriteContent('');
                setEditingRewriteId(0);
            } else {
                setEditingRewriteIndex(index);
                setEditedRewriteContent(record.rewrite_result[index].content);
                setEditingRewriteId(record.rewrite_result[index].id);
            }
        };

        const handleRewriteContentChange = (e) => {
            setEditedRewriteContent(e.target.value);
        };

        const handleRewriteSave = async (e, index, id) => {
            e.stopPropagation();
            setIsSaving(true);

            try {
                // Create a new array with the updated content
                const updatedRewriteResults = [...record.rewrite_result];
                updatedRewriteResults[index] = {
                    ...updatedRewriteResults[index],
                    content: editedRewriteContent,
                    update_time: new Date().toISOString()
                };
                
                // const updatedRecord = {
                //     ...record,
                //     rewrite_result: updatedRewriteResults
                // };
                
                // Update the record in storage
                await updateRewriteResult(id, editedRewriteContent);
                
                // Update local state
                onUpdateContent(record.id, record.content, updatedRewriteResults);
                
                setEditingRewriteIndex(-1);
                setEditedRewriteContent('');
                setEditingRewriteId(0);
            } catch (error) {
                console.error('Error saving rewrite content:', error);
            } finally {
                setIsSaving(false);
            }
        };

        const openVoiceModal = (e) => {
            e.stopPropagation();
            setIsVoiceModalOpen(true);
        };

        const openVoiceSettingsModal = (e, id) => {
            e.stopPropagation();
            setVoiceSettingsId(id);
            setIsVoiceSettingsModalOpen(true);
        };

        const handleRewriteClick = (e) => {
            e.stopPropagation();
            setIsRewriteModalOpen(true);
        };

        const handleRewrite = async (style, customPrompt = '') => {
            setIsRewriting(true);
            
            try {
                const _result = await rewriteContent(record.id, style, customPrompt);

                if (!_result.success) {
                    throw new Error(_result.error);
                }

                // Update local state via parent component
                onUpdateContent(
                    record.id, 
                    record.content, 
                    _result.data.rewrite_result
                );

                dispatchCreditUpdateEvent(_result.data.credit);
                
                // Close modal
                setIsRewriteModalOpen(false);
            } catch (error) {
                console.error('Rewrite error:', error);
                alert('重寫失敗，請稍後再試');
            } finally {
                setIsRewriting(false);
            }
        };

        const generateAudio = async (settings) => {
            setIsGeneratingAudio(true);
            
            try {

                const result = await generateVoice(settings);
                
                if (!result.success) {
                    throw new Error(result.error);
                }

                dispatchCreditUpdateEvent(result.data.credit)
                if (result.data && result.data.url) {
                    const url = result.data.url;
                    
                    if (editingRewriteIndex >= 0 && record.rewrite_result) {
                        const voiceRecord = {
                            id: result.data.id,
                            url: url,
                            voice_id: settings.voiceId,
                            language: settings.languageBoost,
                            speed: settings.speed,
                            volume: settings.volume,
                            pitch: settings.pitch,
                            emotion: settings.emotion,
                            create_time: new Date().toISOString()
                        };
                        
                        const updatedRewriteResults = [...record.rewrite_result];
                        
                        // Initialize generatedVoices array if it doesn't exist
                        if (!updatedRewriteResults[editingRewriteIndex].voices) {
                            updatedRewriteResults[editingRewriteIndex].voices = [];
                        }
                        
                        updatedRewriteResults[editingRewriteIndex].voices.push(voiceRecord);
                        // Update the record in the database
                        await onUpdateContent(record.id, record.content, updatedRewriteResults);
                    
                        onPlayVoice(voiceRecord)
                    } else {
                        // If we're generating audio for the main content
                        const voiceRecord = {
                            id: result.data.id,
                            url: url,
                            voice_id: settings.voiceId,
                            language: settings.languageBoost,
                            speed: settings.speed,
                            volume: settings.volume,
                            pitch: settings.pitch,
                            emotion: settings.emotion,
                            create_time: new Date().toISOString()
                        };

                        const updatedRewriteResults = record.rewrite_result.map(_ => {
                            if (_.id == voiceSettingsId) {
                                if (!_.voices) {
                                    _.voices = []
                                }
                                _.voices.push(voiceRecord);
                            }
                            return _;
                        });
                        
                        // Update the record in the database
                        await onUpdateContent(record.id, record.content, updatedRewriteResults);
                    
                        onPlayVoice(voiceRecord)
                    }
                    
                } else {
                    throw new Error('無法解析語音數據');
                }
            } catch (error) {
                console.error('Generate audio error:', error);
                alert('生成語音失敗，請稍後再試');
            } finally {
                setIsGeneratingAudio(false);
            }
        };

        return (
            <div 
                className={`border overflow-hidden bg-white hover:shadow-md transition-shadow ${isSelected ? 'border-indigo-500 shadow-md' : 'border-gray-200'} rounded-lg`}
                data-name="history-record"
            >
                <RecordHeader 
                    record={record}
                    recordType={recordType}
                    isSelected={isSelected}
                    isExpanded={isExpanded}
                    onToggle={onToggle}
                    onSelect={onSelect}
                    onCopy={onCopy}
                />

                {isExpanded && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50" data-name="record-content">
                        { record.type != 'pdf_to_excel' ? <RecordUrl url={record.source} /> : <></> }
                        
                        { record.type == 'pdf_to_excel' ? <RecordDownload record={record} /> : <RecordContent 
                            content={record.content}
                            isEditing={isEditing}
                            editedContent={editedContent}
                            isSaving={isSaving}
                            isRewriting={isRewriting}
                            onEditToggle={handleEditToggle}
                            onSave={handleSave}
                            onContentChange={handleContentChange}
                            onRewriteClick={handleRewriteClick}
                        /> }
                        
                        {isRewriting && (
                            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 flex items-center" data-name="rewriting-indicator">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                                <span className="text-blue-700">內容重寫生成中，請稍候...</span>
                            </div>
                        )}
                        
                        {record.rewrite_result && record.rewrite_result.length > 0 && (
                            <RewriteResultsList 
                                results={record.rewrite_result}
                                editingRewriteIndex={editingRewriteIndex}
                                editedRewriteContent={editedRewriteContent}
                                isSaving={isSaving}
                                onRewriteEditToggle={handleRewriteEditToggle}
                                onRewriteContentChange={handleRewriteContentChange}
                                onRewriteSave={handleRewriteSave}
                                onCopy={onCopy}
                                openVoiceSettingsModal={openVoiceSettingsModal}
                                openVoiceModal={openVoiceModal}
                                hasApiKey={hasApiKey}
                                showAvatarGenerateModel={showAvatarGenerateModel}
                                currentPlayingId={currentPlayingId}
                                isPlaying={isPlaying}
                                onPlayVoice={onPlayVoice}
                            />
                        )}
                    </div>
                )}

                {isVoiceModalOpen && (
                    <VoiceCloneModal 
                        onClose={() => setIsVoiceModalOpen(false)}
                        content={editingRewriteIndex >= 0 ? 
                            (isEditing ? editedRewriteContent : record.rewrite_result[editingRewriteIndex].content) : 
                            record.content}
                    />
                )}

                {isVoiceSettingsModalOpen && (
                    <VoiceSettingsModal
                        onClose={() => setIsVoiceSettingsModalOpen(false)}
                        onGenerate={generateAudio}
                        id={voiceSettingsId}
                        clonedVoices={myVoices}
                    />
                )}

                {isRewriteModalOpen && (
                    <RewriteModal
                        onClose={() => setIsRewriteModalOpen(false)}
                        onRewrite={handleRewrite}
                        isLoading={isRewriting}
                    />
                )}
            </div>
        );
    } catch (error) {
        console.error('HistoryRecord component error:', error);
        reportError(error);
        return null;
    }
}

export default HistoryRecord;